import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BrandCard = ({ brand }) => {
  const navigate = useNavigate();

  const getImageUrl = (url) => {
    if (!url) return "https://images.unsplash.com/photo-1599305090598-fe179d501c27?w=800&q=80";
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return url;
    return `/${url}`;
  };

  const handleClick = () => {
    navigate(`/products?brand=${brand.b_id}`);
  };

  return (
    <div 
      className="flex max-w-[150px] flex-col items-center text-center space-y-4 group cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] group-hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] group-hover:-translate-y-2 transition-all duration-500 p-5 overflow-hidden ring-4 ring-slate-50/50">
        <img 
          src={getImageUrl(brand.image)} 
          alt={brand.name} 
          className="max-w-full max-h-full object-contain transform group-hover:scale-110 transition-transform duration-700"
        />
      </div>
    </div>
  );
};

export default function BrandsSection({ brands }) {
  const scrollRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const autoScrollRef = useRef(null);

  // Check if content overflows container
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setIsOverflow(el.scrollWidth > el.clientWidth + 4);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [brands]);

  // Auto-scroll when overflow
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isOverflow) return;

    const speed = 1; // px per frame
    let paused = false;

    const step = () => {
      if (!paused) {
        el.scrollLeft += speed;
        // Reset to start when reached end
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
          el.scrollLeft = 0;
        }
      }
      autoScrollRef.current = requestAnimationFrame(step);
    };

    autoScrollRef.current = requestAnimationFrame(step);

    // Pause on hover/touch
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    el.addEventListener('touchstart', pause);
    el.addEventListener('touchend', resume);

    return () => {
      cancelAnimationFrame(autoScrollRef.current);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
    };
  }, [isOverflow]);

  if (!brands || brands.length === 0) return null;

  return (
    <section className="w-full px-2 sm:px-4 pt-2 sm:pt-4 pb-0">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4 sm:mb-6">
        <div className="flex flex-col items-start text-left">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-slate-900 mb-0"
          >
            Our Trusted <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-orange-400">Brands</span>
          </motion.h2>
        </div>

        {isOverflow && (
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' })}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center text-slate-900 border border-slate-200 hover:border-slate-900 transition-all"
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' })}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center text-slate-900 border border-slate-200 hover:border-slate-900 transition-all"
              aria-label="Scroll right"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 sm:gap-6 lg:gap-8 pb-4 overflow-x-auto no-scrollbar justify-center scroll-smooth"
      >
        {brands.map((brand, i) => (
          <div
            key={brand.id || i}
            className="w-[150px] shrink-0 flex justify-center"
          >
            <BrandCard brand={brand} />
          </div>
        ))}
      </div>
    </section>
  );
}
